import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Typography
} from '@mui/material'
import Form from '@rjsf/mui'
import { IChangeEvent } from '@rjsf/mui/dist'
import { RegistryFieldsType } from '@rjsf/utils'
import validator from '@rjsf/validator-ajv8'
import { usePersonalizationActionConfigQuery } from 'apollo/personalization/queries/ActionConfig.generated'
import { PersonalizationAvailableMentionsTypes } from 'apollo/types.generated'
import { galleryImageSelectorFieldRegistry } from 'components/GalleryImageSelector/uiSchema'
import PathTree from 'components/PathTree'
import { formStyles } from 'layout/styles/formStyles'
import { useEffect, useMemo, useState } from 'react'
import { Loading } from 'react-admin'
import { useFormContext } from 'react-hook-form'
import { AsyncTypeaheadField } from 'react-jsonschema-form-extras/lib/TypeaheadField'
import MentionsField from './common/MentionsField'

interface PersonalizationRuleFormActionsProps {
    actions: string
}

const PersonalizationRuleFormActions = ({ actions }: PersonalizationRuleFormActionsProps) => {
    const { setValue } = useFormContext()
    const { data: { actionConfig } = {}, loading } = usePersonalizationActionConfigQuery({
        fetchPolicy: 'network-only'
    })
    const schema = actionConfig && JSON.parse(actionConfig)

    const fieldsRegistry: RegistryFieldsType = useMemo(
        () => ({
            asyncTypeahead: AsyncTypeaheadField,
            ...galleryImageSelectorFieldRegistry
        }),
        []
    )

    // Custom title field template to format array item titles
    const CustomTitleFieldTemplate = useMemo(
        () => (props) => {
            if (props.title && props.title.includes('-')) {
                // Replace "prices-1" with "prices 1", "disabled_urls-2" with "disabled urls 2", etc.
                const customTitle = props.title
                    .replace(/-(\d+)$/, ' $1') // Replace "-number" with " number" (keep original number)
                    .replace(/_/g, ' ') // Replace underscores with spaces

                return (
                    <Typography
                        variant="h5"
                        sx={{ textTransform: 'capitalize', fontSize: '1.3rem' }}
                    >
                        {customTitle}
                    </Typography>
                )
            }
            return (
                <Typography variant="h5" sx={{ textTransform: 'capitalize', fontSize: '1.3rem' }}>
                    {props.title}
                </Typography>
            )
        },
        []
    )

    const uiSchema = useMemo(
        () => ({
            actions: {
                items: {
                    ...schema?.uiSchema?.actions?.items,
                    'ui:TitleFieldTemplate': CustomTitleFieldTemplate,
                    'ui:ArrayFieldTitleTemplate': CustomTitleFieldTemplate,

                    'ui:ObjectFieldTemplate': (props) => {
                        const firstProp = props?.properties[0]?.content?.props
                        const selectedSchema = firstProp?.formData
                        const selectSchemaIndex = firstProp?.schema?.enum.findIndex(
                            (item) => item === selectedSchema
                        )
                        const selectEnumName = firstProp?.schema?.enumNames[selectSchemaIndex]

                        return (
                            <div className="accordion-wrapper">
                                <Accordion defaultExpanded>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography>{selectEnumName || ''} </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {props.properties.map((prop) => (
                                            <Box key={prop.content.key}>{prop.content} </Box>
                                        ))}
                                    </AccordionDetails>
                                </Accordion>
                            </div>
                        )
                    },
                    variable_replace: {
                        overrides: {
                            items: {
                                path: {
                                    'ui:widget': PathTree
                                },
                                'Entity Id': {
                                    'ui:widget': (props) => (
                                        <MentionsField
                                            {...props}
                                            placeholder="Entity Id"
                                            variables={{
                                                type: PersonalizationAvailableMentionsTypes.Retailer,
                                                withEntity: false
                                            }}
                                        />
                                    )
                                },
                                Value: {
                                    'ui:widget': (props) => (
                                        <MentionsField
                                            {...props}
                                            placeholder="Value"
                                            variables={{
                                                type: PersonalizationAvailableMentionsTypes.Retailer,
                                                withEntity: false
                                            }}
                                        />
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }),
        [schema, CustomTitleFieldTemplate]
    )

    const [formData, setFormData] = useState<unknown>(actions && JSON.parse(actions))

    useEffect(() => {
        setValue('actions', actions)
        setFormData(JSON.parse(actions))
        return () => {}
    }, [actions, setValue])

    const onChangeHandler = (e: IChangeEvent<unknown>) => {
        setValue('actions', JSON.stringify(e.formData))
    }

    // Effect to modify array item titles after render
    useEffect(() => {
        const updateArrayTitles = () => {
            // Find all h5 elements that contain hyphens followed by numbers
            const titleElements = document.querySelectorAll('.MuiTypography-h5')
            titleElements.forEach((element) => {
                if (element.textContent && element.textContent.match(/-\d+$/)) {
                    const newText = element.textContent
                        .replace(/-(\d+)$/, ' $1') // Replace "-number" with " number" (keep original number)
                        .replace(/_/g, ' ') // Replace underscores with spaces

                    // Capitalize first letter of each word
                    const capitalizedText = newText
                        .split(' ')
                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(' ')

                    element.textContent = capitalizedText
                }
            })
        }

        // Run immediately and also set up a mutation observer for dynamic changes
        updateArrayTitles()

        const observer = new MutationObserver(() => {
            setTimeout(updateArrayTitles, 100) // Small delay to ensure DOM is updated
        })

        observer.observe(document.body, {
            childList: true,
            subtree: true
        })

        return () => observer.disconnect()
    }, [formData]) // Re-run when form data changes

    if (loading) return <Loading />

    const formWrapperStyles = {
        minWidth: '100%',

        '> form > .field-object > .MuiFormControl-root > .MuiGrid-root.MuiGrid-container > .MuiGrid-item > .field-array .MuiPaper-root > .MuiBox-root > .MuiGrid-container > .MuiGrid-item > .MuiBox-root':
            {
                marginBottom: 0
            },

        '.MuiGrid-item': {
            overflow: 'visible !important'
        },

        '.MuiBox-root': {
            marginTop: 0,
            '> h5.MuiTypography-root': {
                marginBottom: '16px'
            }
        },

        '.form-group.field.field-object .MuiGrid-root.MuiGrid-container': {
            marginTop: '0 !important'
        },

        '& .MuiTypography-root.MuiTypography-h5': {
            textTransform: 'capitalize'
        },

        '& .MuiBox-root[id*="__title"] .MuiTypography-h5': {
            position: 'relative'
        },

        '& .MuiAccordionSummary-root': {
            backgroundColor: '#f5f5f5',
            '& .MuiTypography-root': {
                fontWeight: 'bold'
            }
        },

        '& .MuiDivider-root': {
            margin: '16px 0px'
        },

        '& .MuiCollapse-wrapper': {
            marginTop: '16px'
        },

        '& .MuiGrid-container .MuiGrid-item .MuiPaper-root:has(> .MuiBox-root > .form-group.field.field-object > .MuiFormControl-root > .accordion-wrapper)':
            {
                boxShadow: 'none !important',
                border: 'none !important',
                background: 'transparent !important'
            },

        '& .MuiPaper-root:has(> .MuiBox-root > .form-group.field.field-object .accordion-wrapper)':
            {
                boxShadow: 'none !important',
                border: 'none !important',
                background: 'transparent !important'
            },

        '& .MuiPaper-root:has(> .MuiBox-root > .MuiBox-root[id*="__title"]):not(:has(> .MuiBox-root > .MuiBox-root[id="root_actions__title"]))':
            {
                boxShadow: 'none !important',
                border: 'none !important',
                background: 'transparent !important'
            },

        '& .control-label': {
            fontSize: '24px'
        },

        '& .rbt-input-main': {
            zIndex: 999,
            font: 'inherit',
            letterSpacing: 'inherit',
            color: 'currentColor',
            border: '1px solid grey',
            boxSizing: 'border-box',
            background: 'none',
            height: '1.4375em',
            margin: '5px 0 0 0',
            WebkitTapHighlightColor: 'transparent',
            display: 'block',
            minWidth: 0,
            width: '100%',
            padding: '16.5px 14px',
            borderRadius: '10px'
        },

        '& .rbt-sr-status': {
            marginTop: '5px',
            color: 'inherit'
        },

        '& .rbt-menu': {
            zIndex: 999,
            background: 'white',
            padding: '10px',
            border: '1px solid grey',

            '.dropdown-menu': {
                li: {
                    padding: '10px'
                }
            },

            '.dropdown-item': {
                zIndex: 999,
                textDecoration: 'none',
                cursor: 'pointer',
                color: 'inherit',
                ':hover': {
                    textDecoration: 'underline'
                }
            }
        }
    }

    return (
        <Box sx={formWrapperStyles}>
            <Form
                fields={fieldsRegistry}
                schema={schema}
                uiSchema={uiSchema}
                formData={formData}
                onChange={(e) => onChangeHandler(e)}
                onError={(e) => console.error('errors', e)}
                validator={validator}
                templates={{
                    TitleFieldTemplate: CustomTitleFieldTemplate,
                    ArrayFieldTitleTemplate: CustomTitleFieldTemplate
                }}
            >
                <Button sx={formStyles.hidden} type="submit" />
            </Form>
        </Box>
    )
}

export default PersonalizationRuleFormActions
